/* eslint-disable i18next/no-literal-string */
import { dayjs } from 'date';
import { forwardRef, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimePicker, type DateTimePickerProps, FormControl, FormHelperText } from 'ui';

export type SelectFieldProps = Omit<DateTimePickerProps, 'onChange'> & {
  required?: boolean;
  name: string;
  label: string;
  autocomplete?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  clearable?: boolean;
  onChange?: (date: string | null) => void;
};

export const DateTimeField = forwardRef<HTMLDivElement, SelectFieldProps>(function DateTimeField(
  {
    label,
    name,
    autocomplete,
    disabled = false,
    required = false,
    helperText,
    size = 'small',
    fullWidth = false,
    clearable = true,
    format = 'DD/MM/YYYY HH:mm',
    onChange,
    ...rest
  },
  ref
) {
  const { control } = useFormContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const { slotProps: pickerSlotProps, ...pickerProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              required: 'This field is required',
            }
          : {}
      }
      render={({ field, formState }) => {
        const error = formState.errors[name];
        const { value, ...restField } = field;
        return (
          <FormControl required={required} error={Boolean(error)} size="small" fullWidth={fullWidth} ref={anchorRef}>
            <DateTimePicker
              label={label}
              disabled={disabled}
              {...restField}
              {...pickerProps}
              format={format}
              ref={ref}
              value={value ? dayjs(value) : null}
              fullWidth={fullWidth}
              onChange={(date) => {
                const isoString = date ? date.toISOString() : null;
                field.onChange(isoString);
                if (onChange) {
                  onChange(isoString);
                }
              }}
              name={autocomplete || name}
              slotProps={{
                ...pickerSlotProps,
                openPickerButton: {
                  ...pickerSlotProps?.openPickerButton,
                  // @ts-expect-error - data-testid is a valid prop, despite not being in the type
                  'data-testid': 'date-time-button',
                },
                popper: {
                  ...pickerSlotProps?.popper,
                  // @ts-expect-error ts does not pick up the type of this ref correctly and thinks it can be null, but in reality its always an HTMLElement
                  anchorEl: () => anchorRef.current,
                },
                textField: {
                  ...pickerSlotProps?.textField,
                  error: Boolean(error),
                  required,
                  size,
                },
                ...(clearable && {
                  clearButton: {
                    ...pickerSlotProps?.clearButton,
                    'data-testid': 'clear-date-time-button',
                  },
                  field: {
                    ...pickerSlotProps?.field,
                    clearable: true,
                    onClear: () => field.onChange(null),
                  },
                }),
              }}
            />
            {error || helperText ? (
              <FormHelperText>{error ? String(error?.message) : helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
});
